import { useMutation } from '@tanstack/react-query';

import { INVESTING_BOARD_QUERY_KEYS } from '@/api/investing-board/queries';
import { queryClient } from '@/app/providers/react-query-provider';

import { toSubscriptionAmount } from './real-path';
import {
  cancelInvestmentInStore,
  createInvestment,
  CreateInvestmentInput,
  MOCK_INVESTOR_ID,
} from './store';
import {
  _postCancelSubscription,
  _postStartSubscription,
  invalidateActiveSubscription,
  invalidateWizardResume,
  resyncStartOnConflict,
  resyncSubscriptionOnConflict,
} from './subscription-api';
import { Investment } from './types';

export interface StartInvestmentPayload {
  fundId: string;
  amount: number;
  currency: CreateInvestmentInput['currency'];
  investorId?: string;
  /**
   * The backend fund id, set only on the real path. Its presence is what picks
   * the store: `fundId` stays the wizard's own fund config id, which the mock
   * store keys on and the subscription routes would reject.
   */
  realFundId?: string | null;
  /**
   * What the subscription is stored as being denominated in, set only on the
   * real path. Separate from `currency`, which is what the amount was formatted
   * with — the wizard can only render a couple of currencies, and a fund
   * denominated in another must still be stored in its own.
   */
  denomination?: string;
}

/**
 * Starts the investor's subscription: a real one on the real path, the local
 * Investment on the mock path. Both answer with the id the wizard navigates to
 * via `?investmentId=`, which is all the caller reads.
 */
export const _startInvestment = async (
  payload: StartInvestmentPayload,
): Promise<{ id: string }> => {
  if (payload.realFundId) {
    return _postStartSubscription({
      fundId: payload.realFundId,
      amount: toSubscriptionAmount(payload.amount),
      denomination: payload.denomination ?? payload.currency,
    });
  }

  return createInvestment({
    fundId: payload.fundId,
    amount: payload.amount,
    currency: payload.currency,
    investorId: payload.investorId ?? MOCK_INVESTOR_ID,
  });
};

export const useStartInvestment = () => {
  return useMutation({
    mutationFn: (payload: StartInvestmentPayload) => _startInvestment(payload),
    onSuccess: (_created, payload) => {
      if (payload.realFundId) {
        invalidateWizardResume(payload.realFundId);
        invalidateActiveSubscription(payload.realFundId);
      }
    },
    onError: (error, payload) => resyncStartOnConflict(error, payload.realFundId),
  });
};

export interface CancelInvestmentPayload {
  investmentId: string;
  /** The backend fund id, set only on the real path — see `StartInvestmentPayload`. */
  realFundId?: string | null;
}

/**
 * Investor cancel (ticket 06): moves the Investment to stage `Rejected` when
 * the derived status still allows it (investor eligibility / sign agreement /
 * awaiting payment) — see docs/ubiquitous-language.md → "Cancel (investor)". A wire already in
 * transit (or any later stage) rejects the mutation instead. On the real path
 * the subscription's own cancel route applies the equivalent rule and the
 * subscription ends up `Cancelled`, which the wizard projects as `Rejected`.
 */
export const useCancelInvestment = () => {
  return useMutation({
    mutationFn: async (payload: CancelInvestmentPayload): Promise<Investment | null> => {
      if (payload.realFundId) {
        await _postCancelSubscription(payload.investmentId);
        return null;
      }

      return cancelInvestmentInStore(payload.investmentId);
    },
    onSuccess: (_result, payload) => {
      if (payload.realFundId) {
        invalidateWizardResume(payload.realFundId);
        invalidateActiveSubscription(payload.realFundId);
        queryClient.invalidateQueries({ queryKey: [INVESTING_BOARD_QUERY_KEYS.mine, 'mine'] });
      }
    },
    onError: (error, payload) => resyncSubscriptionOnConflict(error, payload.realFundId),
  });
};
